import React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Outlet, Link } from 'react-router'
import {
  CustomProvider,
  VStack,
  HStack,
  Center,
  Text,
  ButtonToolbar,
  Button,
} from 'rsuite'
import BranchIcon from '@rsuite/icons/Branch'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import 'rsuite/dist/rsuite.min.css'
import './login.css'

import Homepage from './Homepage/Homepage'
import GenericError from './GenericError/GenericError'
import Login from './Login/Login'
import InvalidSignupError from './InvalidSignupError/InvalidSignupError'
import SignUp from './Signup/SignUp'
import PasswordReset from './PasswordReset/PasswordReset'
import ForgotPassword from './ForgotPassword/ForgotPassword'
import PrivacyPolicy from 'shared/PrivacyPolicy/PrivacyPolicy'
import ViewTransitionNavigate from 'shared/ViewTransitionNavigate'

import * as styles from './login.m.css'

const queryClient = new QueryClient()

export const App: React.FC = (): React.ReactElement => {
  return (
    <VStack style={{ height: '100%' }}>
      <VStack.Item className={styles.header} padding="2rem">
        <HStack display="flex" className={styles.headerContent}>
          <HStack.Item>
            <HStack>
              <Link to="/" viewTransition>
                <BranchIcon style={{ fontSize: '3rem' }} />
              </Link>
              <Link to="/" viewTransition>
                <Text size="2xl" weight="bold">
                  Techferral
                </Text>
              </Link>
            </HStack>
          </HStack.Item>
          <HStack.Item>
            <ButtonToolbar spacing={10}>
              <Button appearance="primary" as={Link} to="/login" viewTransition>
                Login
              </Button>
              <Button appearance="ghost" as={Link} to="/sign-up" viewTransition>
                Sign Up
              </Button>
            </ButtonToolbar>
          </HStack.Item>
        </HStack>
      </VStack.Item>
      <VStack.Item className={styles.bodyContent}>
        <Center h="100%">
          <Outlet />
        </Center>
      </VStack.Item>
      <VStack.Item className={styles.footer} padding="2rem">
        <HStack w="100%" justifyContent="space-between" spacing={20}>
          <HStack.Item>
            <Text size="sm" muted>
              © {new Date().getFullYear()} Techferral
            </Text>
          </HStack.Item>
          <HStack.Item>
            <a href="mailto:contact@techferral.com">Contact Us</a>
          </HStack.Item>
          <HStack.Item>
            <Link to="/privacy" viewTransition>
              Privacy Policy
            </Link>
          </HStack.Item>
        </HStack>
      </VStack.Item>
    </VStack>
  )
}

const components = {
  Panel: {
    defaultProps: {
      className: styles.panel,
    },
  },
  ButtonToolbar: {
    defaultProps: {
      style: { justifyContent: 'center', width: '100%' },
    },
  },
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <GenericError />,
    children: [
      { path: '/', element: <Homepage /> },
      { path: '/login', element: <Login /> },
      { path: '/change-password', element: <PasswordReset /> },
      { path: '/sign-up', element: <SignUp /> },
      { path: '/invalid-sign-up', element: <InvalidSignupError /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/accept-invite', element: <SignUp /> },
      {
        path: '/verify-email',
        element: <ViewTransitionNavigate to="/login" />,
      },
      { path: '/privacy', element: <PrivacyPolicy /> },
    ],
  },
])

export const AppRouter: React.FC = (): React.ReactElement => {
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <CustomProvider components={components}>
          <RouterProvider router={router}></RouterProvider>
        </CustomProvider>
      </QueryClientProvider>
    </React.StrictMode>
  )
}

const container = document.getElementById('app')!
const root = createRoot(container)
root.render(<AppRouter />)
